import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import MarketingHeader from "../../components/argus/MarketingHeader";
import Footer from "../../components/argus/Footer";

const CONTENT = {
  terms: {
    title: "Terms of Service",
    intro: "Plain English. The legalese version is in §10.",
    sections: [
      { h: "1. Use ArgusSecure only on systems you own or are authorized to test.", p: "ArgusSecure is an authorized-testing tool. You agree not to scan any system you do not own or have written permission to test." },
      { h: "2. You keep your data. We process it.", p: "Findings, evidence, and scan results belong to your organization. We process them on your behalf to deliver the service." },
      { h: "3. We try hard. We do not guarantee perfection.", p: "ArgusSecure finds vulnerabilities we know about. We do not guarantee detection of every flaw. No security tool can." },
      { h: "4. Pay your bill, keep your account.", p: "Paid plans renew automatically. You can cancel anytime. If a payment fails twice, we pause scans until it clears." },
      { h: "5. We can suspend abusive accounts.", p: "Accounts caught scanning unauthorized targets, ignoring rate limits, or breaching the AUP will be paused or terminated." },
      { h: "6. Limited liability.", p: "ArgusSecure is provided 'as is.' To the extent permitted by law, our liability is capped at fees paid in the prior 12 months." },
      { h: "7. Governed by California law.", p: "Disputes go to courts of San Francisco County, California." },
      { h: "8. We may update these terms.", p: "We'll email you when we change anything material. Continued use = acceptance." },
      { h: "9. Questions?", p: "legal@argus.security" },
      { h: "10. Full legal text.", p: "Available on request and at /legal/terms.pdf for enterprise customers." },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    intro: "Short version: we collect what we need to scan and bill, and nothing else.",
    sections: [
      { h: "What we collect", p: "Account: name, email, organization. Scans: targets you submit, findings produced, evidence captured. Billing: handled by Stripe — we never see your card." },
      { h: "Why we collect it", p: "To deliver scans, show you findings, bill you correctly, and prevent abuse." },
      { h: "Third-party processors", p: "Stripe (billing), Resend (transactional email), Cloudflare (CDN/WAF), AWS KMS (key custody). No data sold. Ever." },
      { h: "Retention", p: "Scan artifacts: 90 days by default. Audit logs: 12 months. Account record: until you delete it." },
      { h: "Your rights", p: "Access, export, correction, deletion. Email privacy@argus.security. We respond within 30 days (GDPR Art. 12; CCPA §1798.130)." },
      { h: "Security", p: "Argon2id for passwords. AES-256-GCM envelope encryption on evidence. KMS-managed keys with quarterly rotation. Audit log of every privileged action." },
      { h: "Contact", p: "privacy@argus.security · ArgusSecure, c/o GSGD FIELD SOLUTIONS ENGINEERING" },
    ],
  },
  "acceptable-use": {
    title: "Acceptable Use Policy",
    intro: "Read this in 60 seconds. Sign once.",
    sections: [
      { h: "You only scan what you own or are authorized to test.", p: "Unauthorized scanning may violate the Computer Fraud and Abuse Act (US, 18 U.S.C. § 1030), the Computer Misuse Act (UK 1990), and similar laws worldwide." },
      { h: "Domain verification is required for every DAST target.", p: "Two methods: DNS TXT record or a file under /.well-known/. Verification expires after 90 days." },
      { h: "Rate limits exist for a reason.", p: "Default 10 req/s per target. Hard ceiling 50 req/s. Daily cap 50k requests on Free plans." },
      { h: "Don't scan blocked categories.", p: "*.gov, *.mil, financial infrastructure, hospital systems, election infrastructure. Operators may allowlist with documented authorization." },
      { h: "Don't scan internal networks.", p: "Private IP ranges (RFC1918, loopback, link-local) and cloud metadata endpoints are blocked at the scanner layer." },
      { h: "Be a good citizen.", p: "Use the responsible-disclosure channel before publishing findings against third parties." },
      { h: "Violations.", p: "We pause accounts on first material breach and terminate on repeat. We may notify law enforcement when warranted." },
    ],
  },
  "responsible-disclosure": {
    title: "Responsible Disclosure",
    intro: "Found a security issue in ArgusSecure itself? Tell us first. We'll work the fix with you.",
    sections: [
      { h: "Email", p: "security@argus.security" },
      { h: "PGP fingerprint", p: "8F1C 4A7D 2E3B 9F5A 1C0E  6D4B 8A2F 3E7C 9D1A 5B6F (key at /security.txt)" },
      { h: "What to include", p: "Affected component, reproduction steps, impact, your suggested fix if you have one, your contact details." },
      { h: "What you get", p: "Acknowledgement within 1 business day. Triage and remediation timeline within 5. A spot on our Hall of Fame at /trust (with your permission)." },
      { h: "What we ask of you", p: "Don't access data that isn't yours. Don't disrupt the service. Give us a reasonable window to ship a fix before publishing." },
      { h: "Out of scope", p: "DoS / volumetric attacks, social engineering of staff, physical attacks, automated scanner output without an exploitable PoC." },
    ],
  },
  dpa: {
    title: "Data Processing Addendum",
    intro: "Enterprise customers can sign a DPA covering GDPR, UK GDPR, and CCPA processing terms.",
    sections: [
      { h: "Standard contractual clauses", p: "EU 2021/914 module 2 (controller → processor) baked in. UK IDTA addendum on request." },
      { h: "Subprocessors", p: "Listed on /trust/subprocessors. We provide 30-day notice of additions." },
      { h: "Audit rights", p: "Annual SOC 2 report shared under NDA. On-site audits available for enterprise plans." },
      { h: "Sign it", p: "Email legal@argus.security. We will send a counter-signed PDF within 5 business days." },
    ],
  },
};

export default function LegalPage() {
  const { slug } = useParams();
  const data = CONTENT[slug];
  if (!data) return <Navigate to="/legal/terms" replace />;

  return (
    <div className="bg-paper text-ink min-h-screen flex flex-col">
      <MarketingHeader />
      <section className="flex-1 mx-auto max-w-3xl w-full px-4 sm:px-6 lg:px-8 py-16">
        <p className="text-xs uppercase tracking-wider text-ink/40 mb-3">Legal</p>
        <h1 className="font-display text-5xl tracking-tight" data-testid="legal-title">{data.title}</h1>
        <p className="mt-3 text-ink/60 max-w-xl">{data.intro}</p>
        <div className="mt-10 space-y-8" data-testid="legal-body">
          {data.sections.map((s, i) => (
            <div key={i}>
              <h2 className="font-display text-2xl mb-2">{s.h}</h2>
              <p className="text-ink/80 leading-relaxed">{s.p}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 flex flex-wrap gap-3 text-sm">
          {Object.keys(CONTENT).filter((k) => k !== slug).map((k) => (
            <Link key={k} to={`/legal/${k}`} className="text-gold hover:underline" data-testid={`legal-link-${k}`}>
              {CONTENT[k].title} →
            </Link>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
